<template>
  <div>
    <!-- Header -->
    <section class="page-header">
      <div class="container">
        <span class="label-tag">Services</span>
        <h1>How I Can Help</h1>
        <p class="page-header-sub">
          Comprehensive football analysis and strategic advisory services
          tailored to your club's needs.
        </p>
      </div>
    </section>

    <!-- Services Grid -->
    <section class="section section-alt">
      <div class="container">
        <div class="row g-4">
          <div class="col-md-6 col-lg-4" v-for="(service, index) in services" :key="service.title">
            <div class="card-custom service-card">
              <div class="service-icon">
                <svg v-if="index === 0" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#4A7CC9" stroke-width="1.5"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                <svg v-else-if="index === 1" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#4A7CC9" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="12" y1="3" x2="12" y2="21"/><circle cx="8" cy="8" r="1.5" fill="#4A7CC9"/><circle cx="16" cy="16" r="1.5" fill="#4A7CC9"/></svg>
                <svg v-else-if="index === 2" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#4A7CC9" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="12" x2="16" y2="14"/><path d="M4.93 4.93l2.83 2.83"/><path d="M16.24 16.24l2.83 2.83"/></svg>
                <svg v-else-if="index === 3" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#4A7CC9" stroke-width="1.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                <svg v-else-if="index === 4" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#4A7CC9" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <svg v-else width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#4A7CC9" stroke-width="1.5"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/><line x1="14" y1="4" x2="10" y2="20"/></svg>
              </div>
              <h3>{{ service.title }}</h3>
              <p>{{ service.description }}</p>
              <ul class="service-deliverables">
                <li v-for="item in service.deliverables" :key="item">{{ item }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Approach -->
    <section class="section section-dark">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-5">
            <span class="label-tag">Approach</span>
            <h2>Rigour Meets Understanding</h2>
            <div class="divider"></div>
          </div>
          <div class="col-lg-6 offset-lg-1">
            <p class="approach-text">
              Every engagement starts with listening. I take the time to understand
              your club's philosophy, your competitive context, and what success looks
              like for you &mdash; then I apply the analytical rigour to help you get there.
            </p>
            <p class="approach-text">
              My background in both football and corporate leadership means I don't
              just deliver data. I deliver insight that fits within your decision-making
              process, presented in a way that coaches, directors, and executives can
              act on immediately.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="section section-alt">
      <div class="container text-center">
        <h2>Let's Discuss Your Requirements</h2>
        <p class="section-subtitle mx-auto mt-3">
          Every club is different. Get in touch to explore how I can support
          your specific objectives.
        </p>
        <router-link to="/contact" class="btn-primary-custom">Get in Touch</router-link>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'ServicesView',
  data() {
    return {
      services: [
        {
          title: 'Strategic Consulting',
          description: 'Advisory services that bridge football knowledge and organisational strategy, drawing on twenty years of senior leadership and executive-level training with the Barcelona Innovation Hub.',
          deliverables: ['Analysis department setup', 'Workflow & process design', 'Recruitment strategy advisory', 'Technology recommendations']
        },
        {
          title: 'Tactical Analysis',
          description: 'In-depth tactical breakdowns of match footage, examining formations, player movements, pressing triggers, and transitions.',
          deliverables: ['Match tactical reports', 'Formation & shape analysis', 'Pressing & transition analysis', 'Set-piece strategy review']
        },
        {
          title: 'Opposition Analysis',
          description: 'Comprehensive opposition reports built for coaching staff. Proven delivery at EFL, National League, and MLS level.',
          deliverables: ['Pre-match opposition reports', 'Strengths & weaknesses profiles', 'Key player analysis', 'Strategic recommendations']
        },
        {
          title: 'Data Analytics',
          description: 'Statistical analysis using industry-standard platforms, transforming raw data into clear, actionable intelligence.',
          deliverables: ['StatsBomb & Wyscout analysis', 'GPS data interpretation', 'Combined event & tracking data', 'Custom data visualisation']
        },
        {
          title: 'Scouting',
          description: 'Player identification and assessment grounded in analytical frameworks and firsthand match observation.',
          deliverables: ['Player assessment reports', 'Recruitment shortlists', 'Performance profiling', 'Video-supported evaluations']
        },
        {
          title: 'Analytics Software',
          description: 'Custom-built analysis tools including bespoke code windows, event recording systems, and AI-powered player tracking.',
          deliverables: ['Bespoke code windows', 'Event recording tools', 'AI player tracking integration', 'Custom reporting dashboards']
        }
      ]
    }
  }
}
</script>

<style scoped>
.page-header {
  padding: 160px 0 80px;
  background: linear-gradient(160deg, var(--navy-dark) 0%, var(--navy) 100%);
}

.page-header h1 {
  margin-bottom: 1rem;
}

.page-header-sub {
  color: var(--silver);
  font-size: 1.15rem;
  max-width: 540px;
}

.service-card {
  display: flex;
  flex-direction: column;
}

.service-icon {
  margin-bottom: 1.25rem;
}

.service-deliverables {
  list-style: none;
  padding: 0;
  margin: 1rem 0 0;
  border-top: 1px solid var(--border-subtle);
  padding-top: 1rem;
}

.service-deliverables li {
  color: var(--silver);
  font-size: 0.85rem;
  padding: 0.35rem 0;
  padding-left: 1rem;
  position: relative;
}

.service-deliverables li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 5px;
  height: 5px;
  background: var(--accent);
  border-radius: 50%;
  transform: translateY(-50%);
}

.approach-text {
  color: var(--silver-light);
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 1.25rem;
}
</style>
